import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';

import { useLocation, useNavigate } from 'react-router';

import { connect, useDispatch } from 'react-redux';

import { ResponsiveImageProps } from 'PropTypes/consultationWelcomeProps';
import { srcOrNextImageProp } from 'PropTypes/imageProps';

import ConsultationHero from '../Scenes/ConsultationHero';
import StylistHero from '../Scenes/StylistHero';

import * as stylistActions from 'dux/stylist/actions';
import { getIsAuthenticated } from 'dux/auth/selectors';
import {
  getConsultationQuestionForIndex,
  hasBufferedAnswers,
  userShouldHaveSupplementsQuestion,
} from 'dux/consultation/selectors';
import { getIsProseStylist, getStylistAvatar, getStylistName } from 'dux/stylist/selectors';
import * as userSelectors from 'dux/user/selectors';
import { bufferedAnswersActions } from 'dux/consultation/slice';

import { useConsultationCategory } from '../hooks/useConsultationCategory';

const HeroContainer = ({
  onNext,
  isAuthenticated,
  isProseStylist,
  stylistAvatar,
  stylistName,
  fetchAndTrySaveStylist,
  questionIndex,
  showSupplementsWelcome,
  hasStoredAnswers,
  userFirstName,
  title,
  subtitle,
  cta,
  image,
  showProgressTracker,
  routeParams,
}) => {
  const dispatch = useDispatch();
  const consultationCategory = useConsultationCategory();

  useEffect(() => {
    fetchAndTrySaveStylist();
  }, [fetchAndTrySaveStylist]);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returningPropectParams = Boolean(searchParams.get('is-returning'));

  const handleResetAnwserBuffer = () => {
    dispatch(bufferedAnswersActions.clearAnswerBuffer());
    onNext();
  };

  const handleNext = () => {
    if (returningPropectParams) {
      navigate(`/consultation/${consultationCategory}/my-lifestyle/zipcode`);
    } else {
      onNext();
    }
  };

  return isProseStylist ? (
    <ConsultationHero
      hasStoredAnswers={hasStoredAnswers}
      isReturning={returningPropectParams}
      onClick={handleNext}
      onResetStoredData={handleResetAnwserBuffer}
      progressBarIndex={questionIndex}
      routeParams={routeParams}
      showSignin={showSupplementsWelcome ? false : !isAuthenticated && !hasStoredAnswers}
      userFirstName={userFirstName}
      variant={{ title, subtitle, cta, image, showProgressTracker }}
    />
  ) : (
    <StylistHero avatar={stylistAvatar} name={stylistName} onClick={onNext} />
  );
};

HeroContainer.propTypes = forbidExtraProps({
  fetchAndTrySaveStylist: PropTypes.func.isRequired,
  hasStoredAnswers: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isProseStylist: PropTypes.bool.isRequired,
  onNext: PropTypes.func.isRequired,
  questionIndex: PropTypes.number.isRequired,
  showSupplementsWelcome: PropTypes.bool.isRequired,
  stylistAvatar: srcOrNextImageProp,
  stylistName: PropTypes.string,
  userFirstName: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.string.isRequired,
  cta: PropTypes.string.isRequired,
  showProgressTracker: PropTypes.bool,
  image: ResponsiveImageProps.isRequired,
  routeParams: PropTypes.shape({}),
});

HeroContainer.defaultProps = {
  routeParams: null,
  stylistAvatar: null,
  stylistName: null,
  userFirstName: '',
  showProgressTracker: true,
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => {
    const { title, subtitle, cta, showProgressTracker, image } = getConsultationQuestionForIndex(
      state,
      { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }
    );
    return {
      isAuthenticated: getIsAuthenticated(state),
      isProseStylist: getIsProseStylist(state),
      stylistAvatar: getStylistAvatar(state),
      stylistName: getStylistName(state),
      showSupplementsWelcome: userShouldHaveSupplementsQuestion(state, {
        consultationCategory,
        consultationSubsetCategory,
        questionIndex,
        routeParams,
      }),
      hasStoredAnswers: hasBufferedAnswers(state),
      userFirstName: userSelectors.getUserFirstName(state),
      title,
      subtitle,
      cta,
      showProgressTracker,
      image,
    };
  },
  {
    fetchAndTrySaveStylist: stylistActions.fetchAndTrySaveStylist,
  },
  (stateProps, dispatchProps, { onNext, questionIndex }) => ({
    onNext,
    questionIndex,
    ...stateProps,
    ...dispatchProps,
  })
)(HeroContainer);
